import { Injectable } from '@angular/core';
import { PolicyRepositoryInterface } from '../types/interfaces';
import { catchError, Observable, throwError, switchMap, defer, of, tap, map } from 'rxjs';
import { CreatePolicyDTO, EditPolicyDTO, Policy } from '../models/Policy';
import { PolicyByTemplateDTO, PolicyDocumentResponseDTO, TemplatePublishedSummaryDTO } from '../types/dtos';
import { ConstantsService } from 'src/app/constants.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { PolicyTemplate } from '../models/PolicyTemplate';
import { PolicyAcknowledgment, PolicyAcknowledgmentResponse } from '../models/PolicyAcknowledgment';
import { PolicyVersion } from '../models/PolicyVersion';
import { SessionService } from 'src/app/session.service';

@Injectable({ providedIn: 'root' })
export class PolicyRepositoryApi implements PolicyRepositoryInterface {
  private apiUrl = '';
  private clientId = '';
  private readonly defaultHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
  private readonly httpOptions = { headers: this.defaultHeaders, withCredentials: true };
  // Session-lifetime cache so re-entering a course-player document node
  // doesn't refire policies-by-template for a templateId already resolved.
  private readonly policyByTemplateCache = new Map<string, PolicyByTemplateDTO>();

  constructor(
    private http: HttpClient,
    private constantsService: ConstantsService,
    private sessionService: SessionService
  ) {
    this.constantsService.onReady(() => {
      this.clientId = this.sessionService.getClientId();
      this.apiUrl = this.constantsService.getConstant('apiUrl');
    });
  }

  protected handleError(error: any): Observable<never> {
    console.error('PolicyRepositoryApi error:', error.error?.message || error.message || 'Server error');
    return throwError(() => Object.assign(
      new Error(error.error?.message || error.message || 'Something bad happened; please try again later.'),
      { status: error.status }
    ));
  }

  private ensureReady(): Observable<void> {
    return defer(() =>
      this.apiUrl && this.clientId
        ? of(void 0)
        : new Observable<void>(observer => {
            this.constantsService.onReady(() => {
              this.clientId = this.sessionService.getClientId();
              this.apiUrl = this.constantsService.getConstant('apiUrl');
              observer.next();
              observer.complete();
            });
          })
    );
  }

  getPolicies(): Observable<Policy[]> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies?clientId=${this.clientId}`;
    return this.http.get<Policy[]>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),withCredentials: true
    }).pipe(catchError(this.handleError));
  }

  getPolicyById(policyId: string): Observable<Policy> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}`
    return this.http.get<Policy>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),withCredentials: true
    }).pipe(catchError(this.handleError));
  }

  getPolicyAcknowledgement(policyId: string, versionId: string): Observable<PolicyAcknowledgmentResponse[]> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}/versions/${versionId}/acknowledgement`;
    return this.http.get<PolicyAcknowledgmentResponse[]>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),withCredentials: true
    }).pipe(catchError(this.handleError));
  }

  getPolicyAcknowledgementPending(policyId: string, versionId: string): Observable<PolicyAcknowledgment[]> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}/versions/${versionId}/acknowledgement-pending`;
    return this.http.get<PolicyAcknowledgment[]>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),withCredentials: true
    }).pipe(catchError(this.handleError));
  }

  getPolicyTemplates(): Observable<PolicyTemplate[]> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policy-templates`;
    return this.http.get<PolicyTemplate[]>(apiUrl, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  getPolicyVersions(policyId: string): Observable<PolicyVersion[]> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}/versions`;
    return this.http.get<PolicyVersion[]>(apiUrl, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  decryptToken(token: string): Observable<any> {
    return this.ensureReady().pipe(
      switchMap(() => {
        const params = new HttpParams().set('token', token);
        const apiUrl = `${this.apiUrl}/policy/api/v1/token/guest/validate-token`;
        return this.http.get(apiUrl, { params, withCredentials: false });
      }),
      catchError(this.handleError)
    );
  }

  getPolicyPdfBlob(pdfUrl: string): Observable<Blob> {
    return this.http.get(pdfUrl, { responseType: 'blob' }).pipe(
      catchError(this.handleError)
    );
  }

  viewPolicyPDF(policyId: string): Observable<string> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/policy-s3/${policyId}.pdf`;
    return this.http.get(apiUrl, {
      responseType: 'text',
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  viewPolicyPDFforAcknowledgement(policyId: string): Observable<any> {
    throw new Error('Method not implemented.');
  }

  createPolicy(requestBody: CreatePolicyDTO): Observable<Policy> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies`;
    return this.http.post<Policy>(apiUrl, requestBody, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  updatePolicy(policyId: string, requestBody: EditPolicyDTO): Observable<Policy> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}`;
    return this.http.put<Policy>(apiUrl, requestBody, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  requirePolicyAcknowledgement(policyId: string): Observable<void> {
    throw new Error('Method not implemented.');
  }

  policyAcknowledgement(policyId: string, versionId: string): Observable<any> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}/versions/${versionId}/acknowledgement/send-link`
    return this.http.post(apiUrl, {}, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  publishPolicy(policyId: string): Observable<Policy> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/${policyId}/publish`
    return this.http.post<Policy>(apiUrl, {}, {
      withCredentials: true,
    }).pipe(catchError(this.handleError));
  }

  acknowledgementPolicy(token: string): Observable<any> {
    const apiUrl = `${this.apiUrl}/policy/api/v1/policies/guest/acknowledgment`
    return this.http.post(apiUrl, { token: token.replace(/\+/g, '%2B') }, {
      withCredentials: false,
    }).pipe(catchError(this.handleError));
  }

  // ==================== Document Management ====================

  /**
   * Converts a DOCX file to SFDT format (Syncfusion Document Text) for editing
   */
  downloadDocumentAsSFDT(policyId: string, file: File): Observable<any> {
    return this.ensureReady().pipe(
      switchMap(() => {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.post(
          `${this.apiUrl}/policy/api/v1/documents/convert-to-sfdt/${policyId}`,
          formData,
          { withCredentials: true }
        );
      }),
      catchError(this.handleError)
    );
  }

  // TEMPORARY: the backend currently answers with an array of policies for the
  // template (empty array = none). Until it settles on a contract, collapse it
  // to a single DTO — the published one if present, else the first — and
  // `null` when there is nothing. A plain object (old contract) still works.
  getPoliciesByTemplateId(templateId: string): Observable<PolicyByTemplateDTO | null> {
    const cached = this.policyByTemplateCache.get(templateId);
    if (cached) return of(cached);

    return this.ensureReady().pipe(
      switchMap(() => this.http.get<PolicyByTemplateDTO | PolicyByTemplateDTO[] | null>(
        `${this.apiUrl}/policy/api/v1/policies/${templateId}/policies-by-template`,
        { withCredentials: true }
      )),
      map((res) => {
        if (!Array.isArray(res)) return res ?? null;
        return res.find((p) => p.status?.toLowerCase() === 'published') ?? res[0] ?? null;
      }),
      tap((dto) => {
        if (dto?.policyId) this.policyByTemplateCache.set(templateId, dto);
      }),
      catchError(this.handleError)
    );
  }

  getPublishedStatusByTemplates(templateIds: string[]): Observable<TemplatePublishedSummaryDTO> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.post<TemplatePublishedSummaryDTO>(
        `${this.apiUrl}/policy/api/v1/policies/published-by-templates`,
        { templateIds },
        this.httpOptions
      )),
      catchError(this.handleError)
    );
  }

  downloadPolicyPDF(policyId: string): Observable<Blob> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get(
        `${this.apiUrl}/policy/api/v1/policies/${policyId}/download-pdf`,
        {
          responseType: 'blob',
          withCredentials: true
        }
      )),
      catchError(this.handleError)
    );
  }

  downloadDocumentByPolicy(policyId: string): Observable<Blob> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get(
        `${this.apiUrl}/policy/api/v1/documents/download-by-policy/${policyId}`,
        {
          responseType: 'blob',
          withCredentials: true,
          observe: 'response'
        }
      )),
      switchMap((response: any) => of(response.body)),
      catchError(this.handleError)
    );
  }

  generateAndSaveDocumentToFolder(policyId: string): Observable<any> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.post(
        `${this.apiUrl}/policy/api/v1/documents/generate-and-save-to-folder/${policyId}`,
        {},
        this.httpOptions
      )),
      catchError(this.handleError)
    );
  }

  getDocumentPdfSignedUrl(policyDocumentId: string): Observable<string> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get(
        `${this.apiUrl}/policy/api/v1/policy-documents/document-pdf/${policyDocumentId}.pdf`,
        { responseType: 'text', withCredentials: true }
      )),
      catchError(this.handleError)
    );
  }

  getPolicyDocuments(policyId: string): Observable<PolicyDocumentResponseDTO[]> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get<PolicyDocumentResponseDTO[]>(
        `${this.apiUrl}/policy/api/v1/policy-documents/policy/${policyId}`,
        { withCredentials: true }
      )),
      catchError(this.handleError)
    );
  }

  uploadPolicyDocumentEditor(policyId: string, file: File): Observable<any> {
    return this.ensureReady().pipe(
      switchMap(() => {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.put(
          `${this.apiUrl}/policy/api/v1/documents/upload-policy-document-editor/${policyId}`,
          formData,
          { withCredentials: true }
        );
      }),
      catchError(this.handleError)
    );
  }

  downloadPolicyDocument(policyDocumentId: string): Observable<Blob> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get(
        `${this.apiUrl}/policy/api/v1/policy-documents/download/${policyDocumentId}`,
        {
          responseType: 'blob',
          withCredentials: true,
          observe: 'response'
        }
      )),
      switchMap((response: any) => of(response.body)),
      catchError(this.handleError)
    );
  }

  deletePolicy(policyId: string): Observable<void> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.delete<void>(
        `${this.apiUrl}/policy/api/v1/policies/${policyId}`,
        { withCredentials: true }
      )),
      catchError(this.handleError)
    );
  }

  getVersionHistoryPdfUrl(policyId: string, versionId: string): Observable<string> {
    return this.ensureReady().pipe(
      switchMap(() => this.http.get(
        `${this.apiUrl}/policy/api/v1/policies/${policyId}/versions/${versionId}/version-history`,
        { responseType: 'text', withCredentials: true }
      )),
      catchError(this.handleError)
    );
  }

  uploadAdditionalDocument(policyDocumentId: string, file: File): Observable<any> {
    return this.ensureReady().pipe(
      switchMap(() => {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.put(
          `${this.apiUrl}/policy/api/v1/policy-documents/update-additional-document/${policyDocumentId}`,
          formData,
          { withCredentials: true }
        );
      }),
      catchError(this.handleError)
    );
  }
}
